import { Card, EmptyState } from '@personachat/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Brain, Download, Eye, Pencil, UsersRound } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { api } from '../../api/client';
import { lire } from '../../api/call';
import { ErreurChargement, Recherche, Section } from '../../components/bits';
import { DataTable, type Colonne } from '../../components/DataTable';
import { formatDate, formatNombre } from '../../lib/format';
import { useFiltresUrl } from '../../lib/urlState';
import { PageHeader } from '../../pages/PageHeader';

import { PastilleMemoire } from './Confidentialite';

interface LigneMemoire {
  id: string;
  email: string;
  fullName: string | null;
  memoryEnabled: boolean;
  personalSections: number;
  projectsWithMemory: number;
  projectSections: number;
  lastProjectUpdate: string | null;
}

/**
 * Conversations et mémoires (cahier des charges §7.9) : point d'entrée du
 * support et des demandes RGPD. Chaque lecture demande un motif, inscrit au
 * journal ; le travail se fait dans la fiche de l'élève.
 */
export function ConversationsPage() {
  const { valeurs: f, modifier } = useFiltresUrl({ recherche: '', page: '1' });
  const navigate = useNavigate();

  const liste = useQuery({
    queryKey: ['memoires', f],
    queryFn: () =>
      lire(api.GET('/api/admin/memories', { params: { query: { page: Number(f.page) || 1, limit: 25, ...(f.recherche ? { q: f.recherche } : {}) } } })),
    placeholderData: keepPreviousData,
  });
  const eleves = useQuery({
    queryKey: ['eleves', 'conversations', f.recherche],
    queryFn: () => lire(api.GET('/api/admin/users', { params: { query: { search: f.recherche, limit: 10, page: 1 } } })),
    enabled: f.recherche.trim().length >= 2,
    placeholderData: keepPreviousData,
  });

  const ouvrir = (id: string, onglet: 'activite' | 'memoire') => navigate(`/eleves/${id}?onglet=${onglet}`);

  const colonnes: Colonne<LigneMemoire>[] = [
    {
      id: 'eleve',
      header: 'Élève',
      cell: (e) => (
        <div>
          <div className="cell-main">{e.fullName ?? e.email}</div>
          {e.fullName && <div className="cell-sub">{e.email}</div>}
        </div>
      ),
    },
    { id: 'etat', header: 'Mémoire', cell: (e) => <PastilleMemoire active={e.memoryEnabled} /> },
    { id: 'perso', header: 'Sections personnelles', align: 'right', cell: (e) => <span className="num">{formatNombre(e.personalSections)}</span> },
    {
      id: 'projets',
      header: 'Projets avec mémoire',
      align: 'right',
      cell: (e) => (
        <span className="num">
          {formatNombre(e.projectsWithMemory)}
          {e.projectSections > 0 && <span className="cell-sub"> ({formatNombre(e.projectSections)} sections)</span>}
        </span>
      ),
    },
    { id: 'maj', header: 'Projet mis à jour', secondaire: true, cell: (e) => (e.lastProjectUpdate ? formatDate(e.lastProjectUpdate) : <span className="muted">—</span>) },
  ];

  const trouvees = eleves.data?.users ?? [];

  return (
    <>
      <PageHeader
        title="Conversations, mémoires"
        lead="Pour le support et les demandes RGPD. Chaque lecture demande un motif, inscrit au journal d’audit avec votre nom."
      />

      <div className="rgpd-guide">
        <Card className="rgpd-guide__carte">
          <Eye size={18} aria-hidden="true" />
          <div>
            <strong>Support</strong>
            <p>Fiche de l’élève, onglet Activité : cliquez une conversation, indiquez le motif, lisez.</p>
          </div>
        </Card>
        <Card className="rgpd-guide__carte">
          <Pencil size={18} aria-hidden="true" />
          <div>
            <strong>Rectification, effacement</strong>
            <p>Onglet « Mémoire, RGPD » : corrigez ou effacez la section concernée de la mémoire de l’IA.</p>
          </div>
        </Card>
        <Card className="rgpd-guide__carte">
          <Download size={18} aria-hidden="true" />
          <div>
            <strong>Accès, portabilité</strong>
            <p>Même onglet : « Exporter ses données » télécharge un fichier JSON complet.</p>
          </div>
        </Card>
      </div>

      <div className="toolbar" role="search">
        <Recherche
          id="conv-recherche"
          label="Rechercher une élève"
          valeur={f.recherche}
          onChange={(recherche) => modifier({ recherche })}
          placeholder="E-mail ou nom de l’élève"
        />
      </div>

      {f.recherche.trim().length >= 2 && (
        <Section title="Élèves trouvées">
          {eleves.isError ? (
            <ErreurChargement erreur={eleves.error} onReessayer={() => void eleves.refetch()} />
          ) : trouvees.length === 0 && !eleves.isFetching ? (
            <p className="texte-aide">Aucune élève ne correspond.</p>
          ) : (
            <ul className="resultats-eleves">
              {trouvees.map((u) => (
                <li key={u.id}>
                  <div className="resultats-eleves__nom">
                    <span className="cell-main">{u.fullName ?? u.username ?? u.email}</span>
                    <span className="cell-sub">
                      {u.email} · {formatNombre(u.stats.conversations)} conversation{u.stats.conversations > 1 ? 's' : ''}
                    </span>
                  </div>
                  <div className="row">
                    <button type="button" className="lien-discret" onClick={() => ouvrir(u.id, 'activite')}>
                      Conversations
                    </button>
                    <button type="button" className="lien-discret" onClick={() => ouvrir(u.id, 'memoire')}>
                      Mémoire, RGPD
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Section>
      )}

      <Section title="Élèves avec une mémoire IA">
        {liste.isError ? (
          <ErreurChargement erreur={liste.error} onReessayer={() => void liste.refetch()} />
        ) : (
          <DataTable
            libelle="Élèves avec une mémoire IA"
            colonnes={colonnes}
            lignes={liste.data?.students}
            cleLigne={(e) => e.id}
            chargement={liste.isFetching && !liste.data}
            onOuvrir={(e) => ouvrir(e.id, 'memoire')}
            pagination={liste.data ? { page: liste.data.pagination.page, pages: liste.data.pagination.pages, total: liste.data.pagination.total } : undefined}
            onPage={(page) => modifier({ page: String(page) })}
            vide={
              <EmptyState
                icon={f.recherche ? <UsersRound size={26} aria-hidden="true" /> : <Brain size={26} aria-hidden="true" />}
                title={f.recherche ? 'Aucune mémoire pour cette recherche' : 'Aucune mémoire enregistrée'}
              />
            }
          />
        )}
        <p className="note">Le contenu d’une mémoire n’apparaît qu’après l’avoir ouverte avec un motif, dans la fiche de l’élève.</p>
      </Section>
    </>
  );
}
